"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

type CityMap = { slug: string; station: string; name: string };

/**
 * Вебвизор (self-hosted PostHog) на сайте Автопилота — 1:1 со Солнышко
 * (sunsites/src/components/layout/PostHog.tsx): запись сессий + клики + события.
 * Метка site="autopilot" отделяет от сайтов Солнышко в общем PostHog-проекте.
 * Автопилот мультигородской: city/station регистрируем по первому сегменту пути
 * ({slug} города) — чтобы в админ-вебвизоре был срез по городу, как у sunsites.
 * Приём на NEXT_PUBLIC_POSTHOG_HOST (напр. https://posthog.autopilot.group).
 * Хост не задан (локально) → компонент инертен.
 *
 * PII: maskAllInputs — значения полей ввода НЕ пишутся (как на sunsites);
 * произвольный текст можно скрыть атрибутом data-ph-mask.
 */
export function PostHog({ cities = [] }: { cities?: CityMap[] }) {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  const pathname = usePathname();
  const active = Boolean(key && host && /^https?:\/\//.test(host));

  // Обновляем city/station при каждой навигации — первый сегмент пути = slug
  // города ("/moscow", "/moscow/...", "/rent/..." → город из ?city в поиске нет,
  // берём slug). Homepage / rent (без города) → пустой station.
  useEffect(() => {
    if (!active) return;
    const ph = (window as { posthog?: { register?: (p: Record<string, string>) => void } }).posthog;
    if (!ph?.register) return;
    const seg = (pathname || "/").split("/").filter(Boolean)[0] || "";
    const city = cities.find((c) => c.slug === seg);
    ph.register({ site: "autopilot", station: city?.station || "", city: city?.name || "" });
  }, [pathname, active, cities]);

  if (!active) return null;

  return (
    <Script id="posthog-js" strategy="afterInteractive">
      {`
        !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
        posthog.init(${JSON.stringify(key)}, {
          api_host: ${JSON.stringify(host)},
          autocapture: true,
          capture_pageview: true,
          capture_pageleave: true,
          person_profiles: "always",
          session_recording: { maskAllInputs: true, maskTextSelector: "[data-ph-mask]" },
          loaded: function (ph) {
            ph.register({ site: "autopilot" });
          }
        });
      `}
    </Script>
  );
}
